import { Component, OnInit, ChangeDetectionStrategy, HostListener, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { UploadFile, UploadOutput } from 'ngx-uploader-esb';
import { ToastrService } from 'ngx-toastr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';
import moment from 'moment';

import { ItemTabComponent } from '../../_basic/tab-playlist-item.component';
import { PlaylistItemType } from '../../../core/models/enums/playlist-item-type.enum';
import { Launcher } from '../../../core/models/entities/launcher.type';
import { AppConstants } from '../../../app.constants';
import { LauncherPath } from '../../../core/models/entities/launcher-path.type';
import { PlaylistItemDTO } from '../../../core/models/view-models/playlist-item-models/playlist-item-dto.model';
import { CustomFileModel } from '../../../core/models/entities/file.type';
import { FileUploadingEnum } from '../../../core/enums/file-uploading.enum';
import { ToastrMessageEnum } from '../../../core/enums/toastr-messages/toastr-message.enum';
import { PlaylistItemLinkModel } from '../../../core/models/view-models/playlist-item-models/playlist-item-link.model';
import { RssTickerConfigs } from '../../../core/models/entities/rss-ticker.type';

@Component({
  selector: 'tab-url',
  templateUrl: './tab-url.component.html',
  styleUrls: ['../../modal-playlist-item/playlist-item-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabUrlComponent extends ItemTabComponent implements OnInit, OnDestroy {
  defaultLauncher: Launcher;
  iconsPath = AppConstants.LAUNCHERS_ICONS_PATH;
  rssInputPlaceholder = AppConstants.RSS_INPUT_PLACEHOLDER;

  private uploadedFile: UploadFile;

  @ViewChild('url') urlInputField: ElementRef;

  constructor(
    private readonly toastrService: ToastrService
  ) {
    super();

    this.filesService.uploadInput$
      .pipe(takeUntilDestroyed())
      .subscribe((uploadData) => {
        this.uploadInput.emit(uploadData)
      });
  }

  @HostListener('window:keydown', ['$event'])
  onKeyPress(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.keyCode === AppConstants.A_LETTER_KEY_CODE) {
      event.preventDefault();
      (this.urlInputField.nativeElement as HTMLInputElement).select();
    }
  }

  ngOnInit(): void {
    if (!this.selectedLauncher && !this.playlist.IsAndroid) {
      this.updateSelectedLauncher(this.launchers);
    }

    this.recentFiles$ = this.filesService.getRecentAndPlaylistItemFiles(this.playlistItem.PlaylistItemLinks.map(source => source.File));
    this.initItems();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  updateSelectedLauncher(launchers: Launcher[]): void {
    this.defaultLauncher = launchers.find(launcher =>
      launcher.ExecutableName?.toLowerCase().includes(AppConstants.CHROME_LAUNCHER_TITLE_PREFIX)
    );

    if (this.playlistItem.Launcher && this.playlistItem.Launcher.Id !== 0) {
      this.selectedLauncher = this.playlistItem.Launcher;
    } else if (this.playlistItem.LocalLauncher && this.playlistItem.Launcher && this.playlistItem.Launcher.Id !== 0) {
      this.selectedLauncher = this.launchers.find(x => _.some(x.Paths,
        (launcherPath: LauncherPath) =>
          this.playlistItem.LocalLauncher &&
          this.playlistItem.LocalLauncher.includes(launcherPath.Path)
      ));
    } else if (this.defaultLauncher) {
      this.selectedLauncher = this.defaultLauncher;
      this.playlistItem.LauncherId = this.defaultLauncher.Id;
    }
  }

  submit() {
    this.playlistItem.PlaylistItemTitle = this.playlistItem.PlaylistItemLinks.length > 1 ?
      this.playlistItemName || AppConstants.MULTIPLE_ITEMS_TITLE :
      this.playlistItem.PlaylistItemLinks[0].Link;

    this.playlistItem.Launcher = this.selectedLauncher;
    this.playlistItem.IsLocal = false;

    this.playlistItem.PlaylistItemLinks.forEach(source => {
      source.InnerItemLinks = [...source.InnerItemLinks.filter(switchItem => switchItem.Link)];
    });
    super.saveChanges([this.playlistItem]);
  }

  onLauncherChanged(event: Launcher): void {
    this.onLauncherChange(event, false, this.playlistItem);
    this.calculateAndSetDuration(this.playlistItem);
  }

  onExactDurationChange(isChecked: boolean): void {
    this.playlistItem.Duration = isChecked
      ? this.playlistItem.ExactVideoDuration
      : AppConstants.DEFAULT_PLAYLIST_ITEM_DURATION;
  }

  onUploadFile(output: UploadOutput, index: number): void {
    switch (output.type) {
      case FileUploadingEnum.AllAddedToQueue:
        this.filesService.startUpload([this.uploadedFile]);
        break;

      case FileUploadingEnum.AddedToQueue:
        if (output.file) {
          this.uploadedFile = output.file;
        }
        break;

      case FileUploadingEnum.Uploading:
        if (output.file) {
          this.playlistItem.PlaylistItemLinks[index].percentage = AppConstants.ZERO_DOWNLOADED_FILE;
          this.playlistItem.PlaylistItemLinks[index].percentage = output.file.progress.data.percentage;
        }
        break;

      case FileUploadingEnum.Done:
        if (output.file.responseStatus === AppConstants.SUCCSESS_REQUEST_RESPONSE_STATUS) {
          const uploadedFile: CustomFileModel = output.file.response;

          this.recentFiles$ = this.filesService.addFileToTheRecentFilesList(this.recentFiles$, uploadedFile);
          this.playlistItem.PlaylistItemLinks[index].File = uploadedFile;
          this.playlistItem.PlaylistItemLinks[index].FileId = uploadedFile.Id;
          this.playlistItem.PlaylistItemLinks[index].duration = uploadedFile.Duration;

          this.calculateAndSetDuration(this.playlistItem);
        }
        else {
          this.toastrService.error(output.file.response.Message, `${ToastrMessageEnum.FailedToUploadTitle}: ${output.file.name}`);
          this.playlistItem.PlaylistItemLinks[index].percentage = AppConstants.ZERO_DOWNLOADED_FILE;
          this.playlistItem.PlaylistItemLinks[index].FileId = null;
        }
        break;
    }
  }

  private initItems() {
    if (!this.playlistItem.PlaylistItemLinks?.length || this.playlistItem.PlaylistItemType !== PlaylistItemType.Url) {
      this.playlistItem = new PlaylistItemDTO();
      this.playlistItem.PlaylistItemType = PlaylistItemType.Url;
      this.playlistItem.PlaylistItemLinks.push(new PlaylistItemLinkModel());
      this.playlistItem.PlaylistId = this.playlist.Id;
      this.playlistItem.LauncherId = this.defaultLauncher?.Id || this.selectedLauncher?.Id;
      this.playlistItem.IsMuted = true;
      this.playlistItem.IsContentStretched = true;
      this.playlistItem.RssConfigs = this.initializeRssConfigs(this.playlist);
      this.setDuration(this.playlistItem, moment.duration(this.playlistItem.ExactVideoDuration).asSeconds(), true);
    }

    if (this.playlistItem.PlaylistItemLinks?.length > 1) {
      this.playlistItemName = this.playlistItem.PlaylistItemTitle;
    }

    this.playlistItem.RssConfigs = this.playlistItem.RssConfigs || new RssTickerConfigs();
    this.initDurationsList(this.playlistItem);
  }
}
